import { MessageContext } from '@/contexts/MessageContext';
import { appApiPath } from '@/lib/share';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  IAction,
  IMessageElement,
  IStep,
  IThread,
  nestMessages,
  useConfig
} from '@chainlit/react-client';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Loader } from '@/components/Loader';
import { Messages } from '@/components/chat/Messages';
import { Translator } from '@/components/i18n';
import { useLayoutMaxWidth } from 'hooks/useLayoutMaxWidth';

/**
 * ``nestMessages`` silently drops steps whose parentId is missing from the
 * list. Share payloads strip tool/run CoT steps, so clear those orphans before
 * nesting — otherwise the whole assistant reply (tables, widgets)
 * never appears.
 */
function clearOrphanParentIds(steps: IStep[]): IStep[] {
  const ids = new Set(steps.map((s) => s.id).filter(Boolean));
  return steps.map((step) => {
    if (step.parentId && !ids.has(step.parentId)) {
      return { ...step, parentId: undefined };
    }
    return step;
  });
}

/**
 * Public share viewer — one-shot snapshot (no live polling).
 * Matches the product goal: share a formatted answer / quiz / plan once.
 */
export default function SharedThreadPage() {
  const { token } = useParams();
  const { t } = useTranslation();
  const { config } = useConfig();
  const layoutMaxWidth = useLayoutMaxWidth();
  const [thread, setThread] = useState<IThread | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) {
      setError(t('sharePage.missing'));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        appApiPath(`/api/share/${encodeURIComponent(token)}`),
        {
          credentials: 'omit'
        }
      );
      if (!res.ok) {
        const fatal =
          res.status === 404
            ? t('sharePage.invalid')
            : t('sharePage.loadFailed');
        setThread(null);
        setError(fatal);
        return;
      }
      const data = (await res.json()) as IThread;
      setThread(data);
      setError(null);
    } catch (e: any) {
      setError(e?.message || t('sharePage.loadFailed'));
      toast.error(t('sharePage.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const onElementRefClick = useCallback((element: IMessageElement) => {
    void element;
  }, []);

  const onError = useCallback((msg: string) => toast.error(msg), []);

  const memoizedContext = useMemo(
    () => ({
      // Never render untrusted shared HTML, even for logged-in viewers.
      allowHtml: false,
      latex: config?.features?.latex,
      editable: false,
      loading: false,
      showFeedbackButtons: false,
      uiName: config?.ui?.name || 'FonGPT',
      cot: 'hidden' as const,
      onElementRefClick,
      onError,
      onFeedbackUpdated: undefined,
      onFeedbackDeleted: undefined
    }),
    [config?.ui?.name, config?.features?.latex, onElementRefClick, onError]
  );

  const steps: IStep[] = clearOrphanParentIds(thread?.steps || []);
  const elements = (thread?.elements || []) as IMessageElement[];
  const actions: IAction[] = [];
  const messages = nestMessages(steps);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background">
      <header className="border-b px-4 py-3">
        <div
          className="mx-auto flex w-full flex-col gap-1"
          style={{ maxWidth: layoutMaxWidth }}
        >
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            <Translator path="sharePage.badge" />
          </p>
          <h1 className="truncate text-lg font-semibold">
            {thread?.name || 'FonGPT'}
          </h1>
          <p className="text-sm text-muted-foreground">
            <Translator path="sharePage.subtitle" />
          </p>
        </div>
      </header>

      <div className="flex w-full flex-1 flex-col overflow-y-auto">
        {loading ? (
          <div className="flex flex-grow items-center justify-center p-8">
            <Loader className="!size-6" />
          </div>
        ) : error && !thread ? (
          <div
            className="mx-auto w-full p-8 text-center text-muted-foreground"
            style={{ maxWidth: layoutMaxWidth }}
          >
            {error}
          </div>
        ) : (
          <ErrorBoundary>
            <MessageContext.Provider value={memoizedContext}>
              <div
                className="mx-auto flex w-full flex-grow flex-col p-4"
                style={{ maxWidth: layoutMaxWidth }}
              >
                <Messages
                  indent={0}
                  messages={messages}
                  elements={elements as any}
                  actions={actions}
                />
              </div>
            </MessageContext.Provider>
          </ErrorBoundary>
        )}
      </div>
    </div>
  );
}
